import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { TbInfoCircle } from 'react-icons/tb';
import type { LocationEntry } from '../../../../hooks/useGetLocations';
import {
    SettingsCard,
    SettingsCheckTable,
    SettingsFlowSplit,
    SettingsRow,
} from '../../../../components/settings/primitives';
import OemMakeSearchField from './OemMakeSearchField';
import type { OemDraft, OemOption } from './oemDraft';

/** Sentinel id for the "All Locations" row in the locations table. */
const ALL_LOCATIONS_ROW = '__all__';

export interface OemMakeStepProps {
    draft: OemDraft;
    onChange: (next: OemDraft) => void;
    isEditMode: boolean;
    options: OemOption[];
    /** Makes already configured, flagged in the search and the similar list. */
    addedMakeIds: Array<string>;
    locations: LocationEntry[];
    onSelectMake: (option: OemOption) => void;
    onClearMake: () => void;
}

/**
 * First step: which make, and where it is certified. The make and the other
 * makes from the same OEM are on the left; the locations holding the
 * certification are on the right, so "who" and "where" are answered together —
 * the same shape as the carrier and vendor screens.
 *
 * Similar makes start checked: an OEM's makes are normally certified together,
 * and each one still saves as its own entry.
 */
const OemMakeStep = ({
    draft,
    onChange,
    isEditMode,
    options,
    addedMakeIds,
    locations,
    onSelectMake,
    onClearMake,
}: OemMakeStepProps) => {
    const hasMake = draft.makeId.length > 0;
    const selectedOption = options.find((option) => option._id === draft.makeId);
    const similarMakes = selectedOption?.similarMakes ?? [];
    const added = new Set(addedMakeIds);
    const allSelected =
        locations.length > 0 &&
        locations.every((location) => draft.locationIds.includes(location._id as string));

    const handleToggleLocation = (id: string, checked: boolean) => {
        if (id === ALL_LOCATIONS_ROW) {
            onChange({
                ...draft,
                locationIds: checked ? locations.map((location) => location._id as string) : [],
            });
            return;
        }
        onChange({
            ...draft,
            locationIds: checked
                ? Array.from(new Set([...draft.locationIds, id]))
                : draft.locationIds.filter((locationId) => locationId !== id),
        });
    };

    const handleToggleSimilar = (id: string, checked: boolean) => {
        onChange({
            ...draft,
            similarMakeIds: checked
                ? Array.from(new Set([...draft.similarMakeIds, id]))
                : draft.similarMakeIds.filter((makeId) => makeId !== id),
        });
    };

    const left = (
        // A flex column, not a scrolling box: the similar makes table takes
        // whatever height is left over and scrolls on its own.
        <VStack align="stretch" spacing={6} flex={1} minH={0} px={6} pb={6} overflow="hidden">
            {hasMake ? (
                <SettingsCard title="Make">
                    <SettingsRow
                        label={draft.name}
                        control={
                            isEditMode ? undefined : (
                                <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={onClearMake}
                                >
                                    Change
                                </Button>
                            )
                        }
                    />
                </SettingsCard>
            ) : (
                // Not a SettingsCard: the card clips its content, which would
                // cut off the search results hanging below the input.
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Make
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the makes Yaxle knows and pick one.
                    </Text>
                    <OemMakeSearchField
                        options={options}
                        addedMakeIds={addedMakeIds}
                        onSelect={onSelectMake}
                        autoFocus
                    />
                    <HStack spacing={2} align="flex-start" mt={4}>
                        <Icon
                            as={TbInfoCircle as React.ElementType}
                            boxSize={4}
                            color="gray.400"
                            mt="2px"
                            flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.500">
                            Orders at certified locations are never auto-ordered, so choosing all OEM
                            parts stays a decision you make.
                        </Text>
                    </HStack>
                </Box>
            )}

            {hasMake && (
                <Box flex={1} minH={0} display="flex" flexDirection="column">
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1} flexShrink={0}>
                        Related makes
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5} flexShrink={0}>
                        Other makes from the same OEM. Checked makes are saved with the same
                        certifications and rebate settings; uncheck any that differ.
                    </Text>
                    <SettingsCheckTable
                        fill
                        columnLabel="RELATED MAKE"
                        checkLabel="Include"
                        emptyMessage="No related makes for this OEM"
                        rows={similarMakes.map((make) => ({
                            id: make._id,
                            label: make.name,
                            isChecked: draft.similarMakeIds.includes(make._id),
                            badge: added.has(make._id) ? 'Already added' : undefined,
                        }))}
                        onToggle={handleToggleSimilar}
                    />
                </Box>
            )}
        </VStack>
    );

    return (
        <SettingsFlowSplit
            leftWidth="50%"
            leftTitle={isEditMode ? 'Make' : 'Add make'}
            left={left}
            isLeftFlush
            // The locations pane waits on a make, the same way the vendor and
            // carrier screens wait before offering their tables.
            rightTitle={hasMake ? 'Applicable locations' : undefined}
            rightDescription={
                hasMake ? 'Which of your shops hold this OEM certification.' : undefined
            }
            right={
                hasMake ? (
                    <Box flex={1} minH={0} px={6} pb={6} display="flex" flexDirection="column">
                        <SettingsCheckTable
                            fill
                            columnLabel="LOCATION"
                            checkLabel="Certified"
                            emptyMessage="No locations configured yet"
                            rows={[
                                {
                                    id: ALL_LOCATIONS_ROW,
                                    label: 'All Locations',
                                    isChecked: allSelected,
                                    isEmphasis: true,
                                },
                                ...locations.map((location) => ({
                                    id: location._id as string,
                                    label: location.title,
                                    isChecked: draft.locationIds.includes(location._id as string),
                                })),
                            ]}
                            onToggle={handleToggleLocation}
                        />
                    </Box>
                ) : null
            }
            isRightFlush
        />
    );
};

export default OemMakeStep;
